import React, { useState } from "react";
import { Settings } from "lucide-react";
import clsx from "clsx";

/**
 * Day separator band rendered between block rows.
 *
 * Now also a drop target for floating tasks — dropping a floating task here
 * lands it at the day's configured `onsite_time` (default 08:00).
 *
 * Click on the gear icon → opens the day settings popover so producers can
 * override the day's onsite time / notes.
 */
export default function DateBand({ label, colSpan, dateKey, onDropFloatingForDay, onOpenDaySettings, onsiteTime }) {
  const [isOver, setIsOver] = useState(false);

  const acceptDrop = (e) => {
    if (!onDropFloatingForDay) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setIsOver(true);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    if (!onDropFloatingForDay) return;
    try {
      const payload = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (payload.kind === "floating-task" && payload.task_id) {
        onDropFloatingForDay(payload.task_id, dateKey);
      }
    } catch (err) {
      console.warn("DateBand drop payload malformed", err);
    }
  };

  return (
    <div
      style={{ gridColumn: `1 / span ${colSpan}`, height: 22 }}
      className={clsx(
        "border-b border-line-strong border-t border-line flex items-center px-3 mono text-2xs uppercase tracking-[0.2em] text-amber sticky left-0 transition-colors group",
        isOver ? "bg-amber/15 outline-1 outline-dashed outline-amber" : "bg-bg-elevated"
      )}
      onDragOver={acceptDrop}
      onDragEnter={acceptDrop}
      onDragLeave={() => setIsOver(false)}
      onDrop={onDrop}
      data-testid={`date-band-${dateKey || "day"}`}
    >
      ── {label} ──
      {onsiteTime && onsiteTime !== "08:00" && (
        <span className="ml-3 text-cyan tracking-widest normal-case font-normal">
          onsite {onsiteTime}
        </span>
      )}
      <div className="flex-1" />
      {onOpenDaySettings && dateKey && (
        <button
          onClick={(e) => { e.stopPropagation(); onOpenDaySettings(dateKey); }}
          className="opacity-30 group-hover:opacity-100 hover:text-amber transition-opacity"
          title="Day settings — onsite time, notes, crew NA"
          data-testid={`day-settings-${dateKey}`}
        >
          <Settings className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
